<template>
    <div class="op-table-wrapper">

        <!-- Paginazione superiore -->
        <div class="opPaginationContainer top" v-if="showTopPagination">
            <div></div>
            <div class="opPaginationNumberContainer">
                <div class="opPaginationNumber" @click="subtractPage">&lt;</div>
                <div class="opPaginationNumber"
                    v-for="p in pages" :key="p"
                    :class="{ selected: selectedPage === p }"
                    @click="goToPage(p)">{{ p }}</div>
                <div class="opPaginationNumber" @click="addPage">&gt;</div>
                <select class="opItemsPerPage" v-model="xItemsPerPage">
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
            </div>
            <div class="opItemsTotal">Totale: <span class="opItemsTotalText">{{ serverSide ? total : processedItems.length }}</span></div>
        </div>

        <!-- Contenitore con scroll -->
        <div class="op-table-scroll" ref="scrollEl" :style="scrollStyle">
            <div class="op-table" ref="tableEl" :style="tableStyle">

                <!-- Header fisso -->
                <div class="op-row op-header">
                    <slot name="header" />
                </div>

                <!-- Body -->
                <template v-if="pagedItems.length > 0">
                    <slot :items="pagedItems" />
                </template>
                <div class="op-empty-row" v-else>
                    <slot name="empty">
                        <div class="op-empty-default">Nessun elemento da mostrare</div>
                    </slot>
                </div>

            </div>
        </div>

        <!-- Paginazione inferiore -->
        <div class="opPaginationContainer bottom" v-if="showBottomPagination">
            <div></div>
            <div class="opPaginationNumberContainer">
                <div class="opPaginationNumber" @click="subtractPage">&lt;</div>
                <div class="opPaginationNumber"
                    v-for="p in pages" :key="p"
                    :class="{ selected: selectedPage === p }"
                    @click="goToPage(p)">{{ p }}</div>
                <div class="opPaginationNumber" @click="addPage">&gt;</div>
                <select class="opItemsPerPage" v-model="xItemsPerPage">
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                </select>
            </div>
            <div class="opItemsTotal">Totale: <span class="opItemsTotalText">{{ serverSide ? total : processedItems.length }}</span></div>
        </div>

    </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { OP_TABLE_CTX, type OpTableContext, type SortDir, type ColumnConfig, type ResolvedColumn } from './opTable.context';
import { StorageServices } from '@/services/StorageServices';
import { isTouchDevice } from '@/utils/device';
import * as OM from '@/model';

export interface PaginationChangePayload {
    page: number;
    pageSize: number;
    sortKey: string;
    sortDir: SortDir;
}

@Options({
    emits: ['pagination-change'],
    provide() {
        const self = this as OpTable;

        const ctx: OpTableContext = {
            registerColumn(config: ColumnConfig): number {
                self.columns.push({
                    currentWidth: 0,
                    minWidth: 0,
                    maxWidth: Infinity,
                    resizable: config.resizable !== false,
                    _raw: config,
                });
                return self.columns.length - 1;
            },

            updateColumnWidth(colIndex: number, width: number): void {
                const col = self.columns[colIndex];
                if (!col) return;
                col.currentWidth = Math.max(col.minWidth, Math.min(col.maxWidth, width));
                self.persistColWidths();
            },

            resetColumnWidth(colIndex: number): void {
                const col = self.columns[colIndex];
                if (!col) return;
                const containerWidth = (self.$refs.tableEl as HTMLElement)?.offsetWidth || 800;
                const defaultWidth = self.parseWidth(col._raw.width, containerWidth) ?? 100;
                col.currentWidth = Math.max(col.minWidth, Math.min(col.maxWidth, defaultWidth));
                self.persistColWidths();
            },

            getSort(): { key?: string; dir: SortDir } {
                return { key: self.sortKey || undefined, dir: self.sortDir };
            },

            getColumnCount(): number {
                return self.columns.length;
            },

            setSort(key?: string, dir?: SortDir): void {
                self.sortKey = key || '';
                self.sortDir = dir || '';
                self.selectedPage = 1;
                if (self.isServerSide) self.emitPaginationChange();
            },
        };

        return { [OP_TABLE_CTX as any]: ctx };
    },
})
export default class OpTable extends Vue {
    @Prop({ default: () => [] }) items!: any[];
    @Prop({ default: 10 }) itemsPerPage!: number;
    @Prop({ default: 'bottom' }) pagination!: 'top' | 'bottom' | 'both' | 'none';
    @Prop({ required: false }) maxHeight!: string;
    @Prop({ required: false }) name!: string;
    @Prop({ default: false }) serverSide!: boolean;
    @Prop({ default: 0 }) total!: number;
    @Prop({ required: false }) filterVm?: OM.BaseListFilterVm;

    columns: ResolvedColumn[] = [];

    // Stato interno (usato quando filterVm non è passato)
    xsortKey = '';
    xsortDir: SortDir = '';
    xselectedPage = 1;
    xxItemsPerPage = 10;

    // ─── Computed: sort/pagina — leggono da filterVm se presente ─────

    get sortKey(): string {
        if (this.filterVm) return this.filterVm.paginationData?.sortBy?.propertyName || '';
        return this.xsortKey;
    }
    set sortKey(v: string) {
        if (this.filterVm) this.filterVm.paginationData.sortBy.propertyName = v;
        else this.xsortKey = v;
    }

    get sortDir(): SortDir {
        if (!this.filterVm) return this.xsortDir;
        const sortBy = this.filterVm.paginationData?.sortBy;
        if (!sortBy?.propertyName) return '';
        return sortBy.ascending ? 'asc' : 'desc';
    }
    set sortDir(v: SortDir) {
        if (this.filterVm) this.filterVm.paginationData.sortBy.ascending = v === 'asc';
        else this.xsortDir = v;
    }

    get selectedPage(): number {
        if (this.filterVm) return this.filterVm.paginationData?.pageNumber || 1;
        return this.xselectedPage;
    }
    set selectedPage(v: number) {
        if (this.filterVm) this.filterVm.paginationData.pageNumber = v;
        else this.xselectedPage = v;
    }

    get xItemsPerPage(): number {
        if (this.filterVm) return this.filterVm.paginationData?.perPage || 10;
        return this.xxItemsPerPage;
    }
    set xItemsPerPage(v: number) {
        if (this.filterVm) this.filterVm.paginationData.perPage = v;
        else this.xxItemsPerPage = v;
    }

    get isServerSide(): boolean {
        return !!(this.filterVm || this.serverSide);
    }

    // ─── Lifecycle ───────────────────────────────────────────────────

    created() {
        if (!this.filterVm) this.xxItemsPerPage = this.itemsPerPage;
    }

    mounted() {
        this.resolveColumnWidths();
        // Con filterVm il parent ascolta già il deep watch; l'emit serve solo per serverSide classico
        if (this.serverSide && !this.filterVm) this.emitPaginationChange();
    }

    // ─── Watchers ────────────────────────────────────────────────────

    @Watch('items')
    onItemsChange() {
        if (!this.isServerSide) this.selectedPage = 1;
    }

    @Watch('xItemsPerPage')
    onIPPChange() {
        this.selectedPage = 1;
        if (this.isServerSide) this.emitPaginationChange();
    }

    // ─── Computed: ordinamento + paginazione ─────────────────────────

    get processedItems(): any[] {
        if (this.isServerSide) return this.items.slice();

        if (!this.sortKey || !this.sortDir) return this.items.slice();

        const key = this.sortKey;
        const dir = this.sortDir;

        return this.items.slice().sort((a, b) => {
            let av = key.split('.').reduce((acc: any, k) => (acc != null ? acc[k] : acc), a);
            let bv = key.split('.').reduce((acc: any, k) => (acc != null ? acc[k] : acc), b);
            if (av == null) av = '';
            if (bv == null) bv = '';
            const res = av.toString().localeCompare(bv.toString(), undefined, { numeric: true, sensitivity: 'base' });
            return dir === 'asc' ? res : -res;
        });
    }

    get totalPages(): number {
        if (this.isServerSide) return Math.ceil(this.total / this.xItemsPerPage) || 1;
        return Math.ceil(this.processedItems.length / this.xItemsPerPage) || 1;
    }

    get pages(): number[] {
        const total = this.totalPages;
        const cur = Math.min(this.selectedPage, total);

        let lower = cur - 2;
        let upper = cur + 2;

        if (cur === 1) upper += 2;
        else if (cur === 2) upper += 1;

        if (cur === total) lower -= 2;
        else if (cur === total - 1) lower -= 1;

        lower = Math.max(1, lower);
        upper = Math.min(total, upper);

        const result: number[] = [];
        for (let i = lower; i <= upper; i++) result.push(i);
        return result;
    }

    get pagedItems(): any[] {
        if (this.isServerSide) return this.items.slice();

        const page = Math.min(this.selectedPage, this.totalPages);
        const start = this.xItemsPerPage * (page - 1);
        return this.processedItems.slice(start, start + this.xItemsPerPage);
    }

    // ─── Computed: stile ─────────────────────────────────────────────

    get tableStyle(): Record<string, string> {
        if (this.columns.length === 0 || this.columns[0].currentWidth === 0) return {};
        return { '--op-grid-cols': this.columns.map(c => `${c.currentWidth}px`).join(' ') };
    }

    get scrollStyle(): Record<string, string> {
        if (!this.maxHeight) return {};
        return { maxHeight: this.maxHeight, overflowY: 'auto' };
    }

    get showTopPagination(): boolean {
        const count = this.isServerSide ? this.total : this.processedItems.length;
        return (this.pagination === 'top' || this.pagination === 'both') && count > this.xItemsPerPage;
    }

    get showBottomPagination(): boolean {
        const count = this.isServerSide ? this.total : this.processedItems.length;
        return (this.pagination === 'bottom' || this.pagination === 'both') && count > this.xItemsPerPage;
    }

    // ─── Paginazione ─────────────────────────────────────────────────

    goToPage(p: number) {
        this.selectedPage = p;
        if (this.isServerSide) this.emitPaginationChange();
    }

    subtractPage() {
        if (this.selectedPage > 1) {
            this.selectedPage--;
            if (this.isServerSide) this.emitPaginationChange();
        }
    }

    addPage() {
        if (this.selectedPage < this.totalPages) {
            this.selectedPage++;
            if (this.isServerSide) this.emitPaginationChange();
        }
    }

    // ─── Server-side ─────────────────────────────────────────────────

    emitPaginationChange() {
        if (this.filterVm) return; // il parent ascolta filterVm direttamente
        this.$emit('pagination-change', {
            page: this.selectedPage,
            pageSize: this.xItemsPerPage,
            sortKey: this.sortKey,
            sortDir: this.sortDir,
        } as PaginationChangePayload);
    }

    resetPage() {
        this.selectedPage = 1;
        if (this.isServerSide) this.emitPaginationChange();
    }

    // ─── Risoluzione larghezze colonne ───────────────────────────────

    resolveColumnWidths() {
        const containerWidth = (this.$refs.tableEl as HTMLElement)?.offsetWidth || 800;
        const saved = (this.name && !isTouchDevice()) ? StorageServices.getOpTableColWidths(this.name) : null;

        this.columns.forEach((col, i) => {
            const raw = col._raw;

            col.minWidth = this.parseWidth(raw.minWidth, containerWidth) ?? 40;
            col.maxWidth = this.parseWidth(raw.maxWidth, containerWidth) ?? Infinity;

            const savedWidth = col.resizable ? (saved?.[i] ?? null) : null;
            if (savedWidth != null) {
                col.currentWidth = Math.max(col.minWidth, Math.min(col.maxWidth, savedWidth));
            } else {
                col.currentWidth = this.parseWidth(raw.width, containerWidth) ?? 100;
            }
        });
    }

    parseWidth(val: number | string | undefined, containerWidth: number): number | undefined {
        if (val == null) return undefined;
        if (typeof val === 'number') return val;
        const s = val.trim();
        if (s.endsWith('%')) return (parseFloat(s) / 100) * containerWidth;
        if (s.endsWith('px')) return parseFloat(s);
        const n = parseFloat(s);
        return isNaN(n) ? undefined : n;
    }

    persistColWidths() {
        if (!this.name) return;
        StorageServices.setOpTableColWidths(this.name, this.columns.map(c => c.resizable ? c.currentWidth : null));
    }
}
</script>
